import React, { useState, useEffect } from 'react';
import { FolderOpen } from 'lucide-react';

// Services & Config
import { supabase } from './services/supabase';
import { MODULES } from './config/moduleDefs';

// Components
import GenericClientModule from './components/modules/GenericClientModule';
import Dashboard from './components/modules/Dashboard';
import StaffWorkloadDashboard from './components/modules/StaffWorkloadDashboard';
import Navbar from './components/layout/Navbar';
import TabBar from './components/layout/TabBar';
import LoginScreen from './components/LoginScreen';

export default function App() {
  const [session, setSession] = useState(null);
  // Default to Dashboard tab
  const [activeModuleId, setActiveModuleId] = useState('dashboard');
  const [dashboardDrilldown, setDashboardDrilldown] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [tabDueCounts, setTabDueCounts] = useState({});

  useEffect(() => {
    // 1. Auth Listener
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));

    const callbackUrl = new URL(window.location.href);
    if (callbackUrl.searchParams.get('code') && callbackUrl.searchParams.get('state')) {
      setActiveModuleId('individual');
    }

    // 2. Global Data Fetch (Staff List used across all modules)
    const fetchStaff = async () => {
        const { data, error } = await supabase.from('staff').select('*');
        if (error) {
            console.error('Error fetching staff:', error);
        }
        if (data) {
            console.log('Staff data fetched:', data); // DEBUG: Log to check column names
            if (data.length > 0) {
                console.log('First staff member:', data[0]); // DEBUG: Show first member object
                console.log('First staff member keys:', Object.keys(data[0])); // DEBUG: Show available columns
            } else {
                console.warn('Staff table is empty');
            }
            setStaffList(data);
        }
    };
    fetchStaff();

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;

    let cancelled = false;

    const fetchTabOverdueCounts = async () => {
      const today = new Date();
      const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
      const getFridayEnd = (date) => {
        const base = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const day = base.getDay();
        const delta = (5 - day + 7) % 7;
        const friday = new Date(base);
        friday.setDate(base.getDate() + delta);
        return endOfDay(friday);
      };

      const thisWeekEnd = getFridayEnd(dayStart);
      const nextWeekEnd = (() => {
        const date = new Date(thisWeekEnd);
        date.setDate(date.getDate() + 7);
        return endOfDay(date);
      })();
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
      const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nextMonthEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 2, 0));
      const in2MonthsStart = new Date(today.getFullYear(), today.getMonth() + 2, 1);
      const in2MonthsEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 3, 0));
      const in3MonthsStart = new Date(today.getFullYear(), today.getMonth() + 3, 1);
      const in3MonthsEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 4, 0));
      const nextCounts = {};

      const createZeroCounts = () => ({
        overdue: 0,
        dueThisWeek: 0,
        dueNextWeek: 0,
        dueThisMonth: 0,
        dueNextMonth: 0,
        dueIn2Months: 0,
        dueIn3Months: 0,
      });

      for (const module of Object.values(MODULES)) {
        if (!module?.id) continue;

        if (!module.tableName) {
          nextCounts[module.id] = createZeroCounts();
          continue;
        }

        const dueFields = Array.from(new Set((module.workstreams || []).map(ws => ws?.dueField).filter(Boolean)));

        if (dueFields.length === 0) {
          nextCounts[module.id] = createZeroCounts();
          continue;
        }

        const selectColumns = typeof module.postFetchFilter === 'function'
          ? '*'
          : `id, ${dueFields.join(', ')}`;

        const { data, error } = await supabase
          .from(module.tableName)
          .select(selectColumns);

        if (error || !data) {
          nextCounts[module.id] = createZeroCounts();
          continue;
        }

        const scopedData = typeof module.postFetchFilter === 'function'
          ? data.filter(record => {
              try {
                return module.postFetchFilter(record);
              } catch {
                return true;
              }
            })
          : data;

        let overdueCount = 0;
        let dueThisWeekCount = 0;
        let dueNextWeekCount = 0;
        let dueThisMonthCount = 0;
        let dueNextMonthCount = 0;
        let dueIn2MonthsCount = 0;
        let dueIn3MonthsCount = 0;
        for (const record of scopedData) {
          let isOverdue = false;
          let isDueThisWeek = false;
          let isDueNextWeek = false;
          let isDueThisMonth = false;
          let isDueNextMonth = false;
          let isDueIn2Months = false;
          let isDueIn3Months = false;

          dueFields.forEach((field) => {
            const raw = record?.[field];
            if (!raw) return;
            const parsed = new Date(raw);
            if (Number.isNaN(parsed.getTime())) return;
            if (parsed < dayStart) isOverdue = true;
            if (parsed >= dayStart && parsed <= thisWeekEnd) isDueThisWeek = true;
            if (parsed > thisWeekEnd && parsed <= nextWeekEnd) isDueNextWeek = true;
            if (parsed >= dayStart && parsed <= monthEnd) isDueThisMonth = true;
            if (parsed >= nextMonthStart && parsed <= nextMonthEnd) isDueNextMonth = true;
            if (parsed >= in2MonthsStart && parsed <= in2MonthsEnd) isDueIn2Months = true;
            if (parsed >= in3MonthsStart && parsed <= in3MonthsEnd) isDueIn3Months = true;
          });

          if (isOverdue) overdueCount += 1;
          if (isDueThisWeek) dueThisWeekCount += 1;
          if (isDueNextWeek) dueNextWeekCount += 1;
          if (isDueThisMonth) dueThisMonthCount += 1;
          if (isDueNextMonth) dueNextMonthCount += 1;
          if (isDueIn2Months) dueIn2MonthsCount += 1;
          if (isDueIn3Months) dueIn3MonthsCount += 1;
        }

        nextCounts[module.id] = {
          overdue: overdueCount,
          dueThisWeek: dueThisWeekCount,
          dueNextWeek: dueNextWeekCount,
          dueThisMonth: dueThisMonthCount,
          dueNextMonth: dueNextMonthCount,
          dueIn2Months: dueIn2MonthsCount,
          dueIn3Months: dueIn3MonthsCount,
        };
      }

      if (!cancelled) {
        setTabDueCounts(nextCounts);
      }
    };

    fetchTabOverdueCounts();
    const intervalId = setInterval(fetchTabOverdueCounts, 120000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [session]);

  // Login Guard
  if (!session) return <LoginScreen onLogin={() => {}} />;

  // Get configuration for the selected tab
  const activeConfig = Object.values(MODULES).find(m => m.id === activeModuleId);
  console.log('App activeModuleId:', activeModuleId, 'activeConfig:', activeConfig?.id);

  const handleDashboardNavigate = ({ moduleId, filter = 'all', source = 'dashboard', ...extra }) => {
    if (!moduleId) return;

    setDashboardDrilldown({
      moduleId,
      filter,
      source,
      ...extra,
      nonce: Date.now(),
    });
    setActiveModuleId(moduleId);
  };

  const handleTabChange = (moduleId) => {
    setActiveModuleId(moduleId);
    if (dashboardDrilldown?.moduleId !== moduleId) {
      setDashboardDrilldown(null);
    }
  };

  const handleReturnToDrilldownSource = () => {
    const source = dashboardDrilldown?.source;
    setDashboardDrilldown(null);
    setActiveModuleId(source === 'staff-workload' ? 'staff_workload' : 'dashboard');
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50 font-sans text-slate-900 overflow-hidden">

      {/* Top Navigation */}
      <div className="flex-none z-[1000]">
        <Navbar
          onLogout={() => supabase.auth.signOut()}
          activeLabel={activeConfig?.label || 'Staff Hub'}
          ActiveIcon={activeConfig?.icon}
          activeColorClass={activeConfig?.colorClass}
          drilldownSource={
            dashboardDrilldown && dashboardDrilldown.moduleId === activeModuleId
              ? dashboardDrilldown.source === 'staff-workload' ? 'Staff Workload' : 'Dashboard'
              : null
          }
        />
      </div>

      {/* Main Module Area */}
        <main className="flex-1 overflow-hidden pb-16 min-h-0">
          <div className="w-full max-w-[1920px] mx-auto px-4 py-4 h-full min-h-0 flex flex-col">
          {activeConfig ? (
            activeModuleId === 'dashboard' ? (
                    <div className="flex-1 overflow-auto">
                      <Dashboard onNavigate={handleDashboardNavigate} staffList={staffList} />
                    </div>
            ) : activeModuleId === 'staff_workload' ? (
                    <div className="flex-1 overflow-auto">
                      <StaffWorkloadDashboard onNavigate={handleDashboardNavigate} staffList={staffList} />
                    </div>
            ) : (
              // The Generic Engine handles the view based on the config prop
              <GenericClientModule
                key={activeConfig.id} // Important: Forces component reset on tab change
                config={activeConfig}
                staffList={staffList}
                dashboardDrilldown={dashboardDrilldown}
                onReturnToDrilldownSource={handleReturnToDrilldownSource}
              />
            )
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
              <FolderOpen className="w-16 h-16 mb-4 text-slate-200" />
              <h3 className="text-xl font-semibold">Module Not Found</h3>
              <p>The requested module ID "{activeModuleId}" is not defined in your configuration.</p>
            </div>
          )}
        </div>
      </main>

      {/* Bottom Tabs */}
        <div className="fixed inset-x-0 bottom-0 z-[1000]">
        <TabBar
          modules={MODULES}
          activeId={activeModuleId}
          onChange={handleTabChange}
          dueCounts={tabDueCounts}
        />
      </div>
    </div>
  );
}